import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import type { Month } from '@/types';
import { formatMonthYear } from '@/lib/format';
import { cn } from '@/lib/cn';
import { PeriodSelector } from './PeriodSelector';
import { HelpModal } from './HelpModal';

const TITLES: Record<string, string> = {
  '/': 'Sales Dashboard',
  '/finance': 'Control Financiero',
  '/inventory': 'Inventario y Producción',
  '/anomalies': 'Auditoría de Anomalías',
  '/trends': 'Tendencias y Proyecciones',
  '/predict': 'Predicción',
  '/upload': 'Carga de Archivos',
  '/config': 'Configuración',
};

interface TopBarProps {
  months: Month[];
  selectedMonthId: string | null;
  onSelectMonth: (id: string) => void;
}

export default function TopBar({ months, selectedMonthId, onSelectMonth }: TopBarProps) {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const title = TITLES[location.pathname] || 'Empanada BI';
  const selectedMonth = months.find((m) => m.id === selectedMonthId);

  return (
    <header
      className={cn(
        'fixed top-0 right-0 w-[calc(100%-260px)] h-16',
        'bg-surface border-b-2 border-outline z-40',
        'flex justify-between items-center px-6',
      )}
    >
      <div className="flex items-center gap-6">
        <h2 className="font-condensed text-2xl uppercase tracking-tight text-text-bright leading-none">
          {title}
        </h2>

        {/* Month selector — kinetic border + offset shadow */}
        {months.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen((v) => !v)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 cursor-pointer',
                'font-mono uppercase tracking-wider text-xs text-primary',
                'bg-surface border-2 border-outline',
                'shadow-[3px_3px_0_0_#000000]',
                'transition-all duration-150',
                'hover:shadow-[4px_4px_0_0_#ff8a65] hover:border-primary',
                isDropdownOpen && 'shadow-[1px_1px_0_0_#000000] translate-x-[2px] translate-y-[2px]',
              )}
            >
              <span className="material-symbols-outlined text-lg">calendar_month</span>
              <span>
                {selectedMonth
                  ? formatMonthYear(selectedMonth.month, selectedMonth.year)
                  : 'Seleccionar mes'}
              </span>
              <span className="material-symbols-outlined text-lg">
                {isDropdownOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>

            {isDropdownOpen && (
              <div
                className={cn(
                  'absolute top-full mt-3 left-0 min-w-[280px] z-50',
                  'bg-surface-container border-2 border-outline',
                  'shadow-[4px_4px_0_0_#000000] p-2',
                )}
              >
                {months.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      onSelectMonth(m.id);
                      setIsDropdownOpen(false);
                    }}
                    className={cn(
                      'w-full text-left px-4 py-3 cursor-pointer',
                      'transition-colors duration-150',
                      'hover:bg-primary/20',
                      m.id === selectedMonthId && 'bg-primary text-black',
                    )}
                  >
                    <div className="font-bold uppercase tracking-wider text-xs">
                      {formatMonthYear(m.month, m.year)}
                    </div>
                    <div
                      className={cn(
                        'font-mono text-[10px] mt-1 uppercase tracking-widest',
                        m.id === selectedMonthId ? 'text-black' : 'text-text-muted',
                      )}
                    >
                      {m.totalDays}d · {m.totalUnits.toLocaleString()} unid
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <PeriodSelector />
        <button
          className={cn(
            'w-10 h-10 flex items-center justify-center cursor-pointer',
            'bg-surface border-2 border-outline text-text-muted',
            'shadow-[3px_3px_0_0_#000000] transition-all duration-150',
            'hover:border-primary hover:text-primary',
          )}
        >
          <span className="material-symbols-outlined">notifications</span>
        </button>
        <button
          onClick={() => setIsHelpOpen(true)}
          aria-label="Abrir ayuda"
          title="Ayuda y glosario"
          className={cn(
            'w-10 h-10 flex items-center justify-center cursor-pointer',
            'bg-surface border-2 border-outline text-text-muted',
            'shadow-[3px_3px_0_0_#000000] transition-all duration-150',
            'hover:border-primary hover:text-primary',
            isHelpOpen && 'border-primary text-primary',
          )}
        >
          <span className="material-symbols-outlined">help</span>
        </button>
      </div>
      <HelpModal open={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </header>
  );
}
